import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';

const formatMetric = (value, unit) => {
  if (unit === '%') return `${Number(value || 0).toFixed(0)}%`;
  if (unit === 'days') return uiText("{{count}} days", { count: Number(value || 0).toFixed(1) });
  return `${Number(value || 0).toFixed(1)}${unit ? ` ${unit}` : ''}`;
};

const SegmentedBarsVisual = ({ model, isRTL = false }) => {
  useUiLanguage();
  const rootDataset = model.dataset;
  const size = model.size || 'medium';
  const [selectedViewId, setSelectedViewId] = useState(rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '');
  const [activeSelection, setActiveSelection] = useState(null);

  useEffect(() => {
    const nextDefault = rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '';
    if (!nextDefault) return;
    setSelectedViewId((currentId) => {
      if (!rootDataset?.views?.[currentId] || currentId !== nextDefault) {
        return nextDefault;
      }
      return currentId;
    });
  }, [rootDataset]);

  const dataset = rootDataset?.views?.[selectedViewId] || rootDataset;
  const rows = dataset?.rows || [];
  const segments = dataset?.segments || [];
  const line = dataset?.line || {};

  const activeDrilldown = useMemo(() => {
    if (!activeSelection) return null;
    const row = rows.find((item) => item.id === activeSelection.rowId);
    if (!row?.drilldown?.[activeSelection.segmentId]) return null;
    const segment = segments.find((item) => item.id === activeSelection.segmentId);
    return {
      row,
      segment,
      items: row.drilldown[activeSelection.segmentId]
    };
  }, [activeSelection, rows, segments]);

  if (!rows.length || !segments.length) return null;

  if (size === 'compact') {
    return (
      <div className="analytics-visual analytics-visual-segmented-compact-list">
        {rows.map((row) => {
          const segmentEntries = segments.map((segment) => ({
            ...segment,
            value: Number(row.segments?.[segment.id] || 0)
          }));
          const strongestSegment = [...segmentEntries].sort((left, right) => right.value - left.value)[0];
          const weakestSegment = [...segmentEntries].sort((left, right) => left.value - right.value)[0];
          return (
            <div key={row.id} className="analytics-visual-segmented-compact-row">
              <div className="analytics-visual-segmented-compact-head">
                <div className="analytics-visual-scatter-list-copy">
                  <span className="analytics-visual-label">{uiText(row.label)}</span>
                  {row.subLabel ? <span className="analytics-visual-sub">{row.subLabel}</span> : null}
                </div>
                <strong className="analytics-visual-segmented-compact-metric">{formatMetric(row.lineValue, line.unit)}</strong>
              </div>

              <div className="analytics-visual-segmented-compact-strip" aria-label={`${row.label} composition`}>
                {segmentEntries.map((segment) => (
                  <span
                    key={segment.id}
                    className={`analytics-visual-segmented-compact-slice ${segment.tone || 'accent'}`}
                    style={{ width: `${segment.value}%` }}
                    title={`${segment.label}: ${Math.round(segment.value)}%`}
                  />
                ))}
              </div>

              <div className="analytics-visual-segmented-compact-foot">
                <span className="analytics-visual-segmented-compact-extreme">
                  <span className={`analytics-visual-legend-swatch ${strongestSegment.tone || 'accent'}`} />
                  <span>{uiText(strongestSegment.label)}</span>
                  <strong>{`${Math.round(strongestSegment.value)}%`}</strong>
                </span>
                <span className="analytics-visual-segmented-compact-extreme">
                  <span>{uiText(weakestSegment.label)}</span>
                  <strong>{`${Math.round(weakestSegment.value)}%`}</strong>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (size === 'medium') {
    return (
      <div className="analytics-visual analytics-visual-segmented-medium-grid">
        {rows.map((row) => {
          const segmentEntries = segments.map((segment) => ({
            ...segment,
            value: Number(row.segments?.[segment.id] || 0)
          }));
          return (
            <div key={row.id} className="analytics-visual-segmented-medium-card">
              <div className="analytics-visual-segmented-medium-head">
                <div className="analytics-visual-scatter-list-copy">
                  <span className="analytics-visual-label">{uiText(row.label)}</span>
                  {row.subLabel ? <span className="analytics-visual-sub">{row.subLabel}</span> : null}
                </div>
                <strong className="analytics-visual-segmented-medium-metric">{formatMetric(row.lineValue, line.unit)}</strong>
              </div>

              <div className="analytics-visual-segmented-medium-strip" aria-label={`${row.label} composition`}>
                {segmentEntries.map((segment) => (
                  <span
                    key={segment.id}
                    className={`analytics-visual-segmented-medium-slice ${segment.tone || 'accent'}`}
                    style={{ width: `${segment.value}%` }}
                    title={`${segment.label}: ${Math.round(segment.value)}%`}
                  />
                ))}
              </div>

              <div className="analytics-visual-segmented-medium-legend">
                {segmentEntries.map((segment) => (
                  <span key={segment.id} className="analytics-visual-segmented-medium-item">
                    <span className={`analytics-visual-legend-swatch ${segment.tone || 'accent'}`} />
                    <span>{uiText(segment.label)}</span>
                    <strong>{`${Math.round(segment.value)}%`}</strong>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const buildSegmentEntries = (row) =>
    segments.map((segment) => ({
      ...segment,
      value: Number(row.segments?.[segment.id] || 0)
    }));

  const metricLabel = line.label || (isRTL ? 'المؤشر' : 'Metric');

  return (
    <div className={`analytics-visual analytics-visual-segmented-bars readable${isRTL ? ' rtl' : ''}`}>
      {rootDataset?.selectors?.length ? (
        <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
          {rootDataset.selectors.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
              onClick={() => setSelectedViewId(option.id)}
            >
              {uiText(option.label)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="analytics-visual-segmented-bars-legend readable">
        {segments.map((segment) => (
          <span key={segment.id} className="analytics-visual-legend-item">
            <span className={`analytics-visual-legend-swatch ${segment.tone || 'accent'}`} />
            <span className="analytics-visual-sub">{uiText(segment.label)}</span>
          </span>
        ))}
      </div>

      <div className="analytics-visual-segmented-readable-list">
        {rows.map((row) => {
          const segmentEntries = buildSegmentEntries(row);
          const dominantSegment = [...segmentEntries].sort((left, right) => right.value - left.value)[0];
          const total = segmentEntries.reduce((sum, segment) => sum + segment.value, 0) || 100;

          return (
            <article key={row.id} className="analytics-visual-segmented-readable-row">
              <div className="analytics-visual-segmented-readable-head">
                <div className="analytics-visual-segmented-readable-title">
                  <span className="analytics-visual-label">{uiText(row.label)}</span>
                  {row.subLabel ? <span className="analytics-visual-sub">{row.subLabel}</span> : null}
                </div>
                <div className="analytics-visual-segmented-readable-metric">
                  <span>{metricLabel}</span>
                  <strong>{formatMetric(row.lineValue, line.unit)}</strong>
                </div>
              </div>

              <div className="analytics-visual-segmented-readable-strip" aria-label={`${row.label} composition`}>
                {segmentEntries.map((segment) => {
                  const clickable = Boolean(row.drilldown?.[segment.id]?.length);
                  return (
                    <button
                      key={segment.id}
                      type="button"
                      className={`analytics-visual-segmented-readable-slice ${segment.tone || 'accent'}${clickable ? ' clickable' : ''}`}
                      style={{ width: `${Math.max(0, (segment.value / total) * 100)}%` }}
                      title={`${segment.label}: ${Math.round(segment.value)}%`}
                      onClick={clickable ? () => setActiveSelection({ rowId: row.id, segmentId: segment.id }) : undefined}
                      disabled={!clickable}
                    />
                  );
                })}
              </div>

              <div className="analytics-visual-segmented-readable-foot">
                <div className="analytics-visual-segmented-readable-dominant">
                  <span className={`analytics-visual-legend-swatch ${dominantSegment.tone || 'accent'}`} />
                  <span>{uiText(dominantSegment.label)}</span>
                  <strong>{`${Math.round(dominantSegment.value)}%`}</strong>
                </div>
                <div className="analytics-visual-segmented-readable-values">
                  {segmentEntries.map((segment) => (
                    <span key={`${row.id}-${segment.id}-value`} className="analytics-visual-segmented-readable-value">
                      <span>{uiText(segment.label)}</span>
                      <strong>{`${Math.round(segment.value)}%`}</strong>
                    </span>
                  ))}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {activeDrilldown ? (
        <div className="analytics-visual-segmented-drilldown">
          <div className="analytics-visual-segmented-drilldown-head">
            <div className="analytics-visual-label">
              {uiText(activeDrilldown.row.label)} · {uiText(activeDrilldown.segment.label)}
            </div>
            <div className="analytics-visual-sub">
              {activeDrilldown.items.length} {isRTL ? 'حالة' : 'hires'}
            </div>
          </div>
          <div className="analytics-visual-segmented-drilldown-grid">
            {activeDrilldown.items.map((item) => (
              <div key={item.id} className="analytics-visual-segmented-drilldown-row">
                <strong>{uiText(item.label)}</strong>
                <span>{item.meta}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SegmentedBarsVisual;
